// Central fix for the benign "ResizeObserver loop completed with undelivered
// notifications" error (see FRONTEND.md § ResizeObserver).
//
// The error is emitted by the browser when a ResizeObserver callback changes the
// size of an observed element inside the same delivery cycle, so the leftover
// notifications roll to the next frame. It is harmless — no crash, no infinite
// loop — but in dev the CRA / webpack-dev-server error overlay listens for global
// error events and pops it onto the screen (production ships no such overlay, so
// it never surfaces there).
//
// Rather than swallow the event, we fix the root cause centrally: wrap the global
// ResizeObserver so EVERY callback (ours and any dependency's — rmsc, MUI, …) runs
// on the next animation frame instead of synchronously inside the observation
// cycle. The size-changing side effect then lands outside that cycle and the loop
// can never spill. This is the same one-frame-defer trick several ResizeObserver
// polyfills use internally; the cost is an imperceptible one-frame delay on resize
// reactions. Because this is global, per-component rAF wrappers are unnecessary.
//
// NOTE: this only covers observers our page's JS constructs. A browser extension's
// content script runs in a separate isolated world with its own ResizeObserver, so
// this patch can't wrap it — an extension that loops still surfaces the (identical)
// benign error on the page window. If you see it in dev with nothing in our code
// creating an observer, reproduce in an extension-free profile before suspecting us.

const Native = typeof window !== 'undefined' ? window.ResizeObserver : undefined;

if (Native && !Native.__bravoRafPatched) {
  const Patched = class extends Native {
    constructor(callback) {
      let rafId = null;
      super((entries, observer) => {
        if (rafId !== null) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          rafId = null;
          callback(entries, observer);
        });
      });
    }
  };
  Patched.__bravoRafPatched = true;
  window.ResizeObserver = Patched;
}
